import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Header from "../components/header";
import DefaultFooter from "../components/default-footer";
import AOS from "aos";
import "aos/dist/aos.css";

// ============================================================
// Small reusable pieces — kept identical to the homepage so the
// two pages read as one product, not two different templates.
// ============================================================

function DuneRule({ tone = "light" }: { tone?: "light" | "dark" }) {
  const stroke = tone === "dark" ? "rgba(255,255,255,.14)" : "rgba(41,75,90,.14)";
  return (
    <div aria-hidden="true" style={{ lineHeight: 0, background: "inherit" }}>
      <svg viewBox="0 0 1200 34" width="100%" height="22" preserveAspectRatio="none">
        <path
          d="M0 24 C 90 4, 170 4, 250 20 S 400 30, 480 14 S 620 2, 700 18 S 850 30, 940 12 S 1090 2, 1200 16"
          fill="none" stroke="var(--gold-d)" strokeOpacity="0.55" strokeWidth="1.5"
        />
        <path
          d="M0 28 C 90 12, 170 12, 250 26 S 400 34, 480 20 S 620 10, 700 24 S 850 34, 940 18 S 1090 10, 1200 22"
          fill="none" stroke={stroke} strokeWidth="1"
        />
      </svg>
    </div>
  );
}

type Category = "visa" | "travel" | "car" | "general";

const CATEGORIES: { id: Category; label: string }[] = [
  { id: "visa", label: "Visa Services" },
  { id: "travel", label: "Tours & Travel" },
  { id: "car", label: "Car Rental" },
  { id: "general", label: "Payments & Support" },
];

function FAQ() {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState<Category | "all">("all");
  const [openId, setOpenId] = useState<number | null>(null);

  // FAQ Items with translations
  const FAQ_ITEMS = useMemo(() => [
    {
      id: 1,
      category: "visa" as Category,
      q: t("faqVisaQ1"),
      a: t("faqVisaA1"),
    },
    {
      id: 2,
      category: "visa" as Category,
      q: t("faqVisaQ2"),
      a: t("faqVisaA2"),
    },
    {
      id: 3,
      category: "visa" as Category,
      q: t("faqVisaQ3"),
      a: t("faqVisaA3"),
    },
    {
      id: 4,
      category: "visa" as Category,
      q: t("faqVisaQ4"),
      a: t("faqVisaA4"),
    },
    {
      id: 5,
      category: "visa" as Category,
      q: t("faqVisaQ5"),
      a: t("faqVisaA5"),
    },
    {
      id: 6,
      category: "travel" as Category,
      q: t("faqTravelQ1"),
      a: t("faqTravelA1"),
    },
    {
      id: 7,
      category: "travel" as Category,
      q: t("faqTravelQ2"),
      a: t("faqTravelA2"),
    },
    {
      id: 8,
      category: "travel" as Category,
      q: t("faqTravelQ3"),
      a: t("faqTravelA3"),
    },
    {
      id: 9,
      category: "car" as Category,
      q: t("faqCarQ1"),
      a: t("faqCarA1"),
    },
    {
      id: 10,
      category: "car" as Category,
      q: t("faqCarQ2"),
      a: t("faqCarA2"),
    },
    {
      id: 11,
      category: "car" as Category,
      q: t("faqCarQ3"),
      a: t("faqCarA3"),
    },
    {
      id: 12,
      category: "general" as Category,
      q: t("faqGeneralQ1"),
      a: t("faqGeneralA1"),
    },
    {
      id: 13,
      category: "general" as Category,
      q: t("faqGeneralQ2"),
      a: t("faqGeneralA2"),
    },
  ], [t]);

  useEffect(() => {
    AOS.init({ duration: 700, easing: "ease-out-quad", once: true });
  }, []);

  const visibleItems = useMemo(
    () => (activeCategory === "all" ? FAQ_ITEMS : FAQ_ITEMS.filter((f) => f.category === activeCategory)),
    [activeCategory, FAQ_ITEMS]
  );

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap');

        :root {
          --teal: #294b5a;
          --teal-d: #1a3140;
          --teal-l: #3b6272;
          --gold: #d3be76;
          --gold-l: #ede4b5;
          --gold-d: #b09a50;
          --cream: #f7f4ee;
          --mist: #edeae2;
          --silver: #8a9aa3;
          --ink: #1a2f38;
          --radius: 10px;
          --radius-sm: 5px;
          --shadow-sm: 0 6px 20px rgba(26, 49, 61, .07);
          --shadow-md: 0 20px 48px rgba(26, 49, 61, .12);
          --shadow-lg: 0 28px 64px rgba(26, 49, 61, .18);
        }
        * { box-sizing: border-box; }
        body { font-family: 'DM Sans', sans-serif; background: var(--cream); -webkit-font-smoothing: antialiased; }
        h1, h2, h3, h4 { font-family: 'Playfair Display', serif; }
        a:focus-visible, button:focus-visible { outline: 2px solid var(--gold-d); outline-offset: 2px; }
        @media (prefers-reduced-motion: reduce) { *, *::before, *::after { animation-duration: .01ms !important; transition-duration: .01ms !important; } }

        .eyebrow { display: inline-flex; align-items: center; gap: 10px; font-size: 11px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; color: var(--gold-d); }
        .eyebrow::before { content: ''; width: 22px; height: 2px; background: var(--gold); border-radius: 2px; }
        .eyebrow.light { color: var(--gold-l); }
        .eyebrow.light::before { background: var(--gold-l); opacity: .6; }

        .section-title { font-size: clamp(2rem, 3.1vw, 2.9rem); font-weight: 600; color: var(--ink); line-height: 1.14; letter-spacing: -.02em; margin: 10px 0 14px; }
        .section-sub { font-size: 15px; color: var(--silver); font-weight: 300; line-height: 1.7; max-width: 520px; }
        .section-pad { padding: 84px 0; }
        @media (max-width: 991px) { .section-pad { padding: 60px 0; } }
        @media (max-width: 576px) { .section-pad { padding: 44px 0; } }

        /* Intro band */
        .faq-intro { background: var(--teal-d); padding: 160px 0 64px; text-align: center; position: relative; overflow: hidden; }
        .faq-intro::before {
          content: '';
          position: absolute; inset: 0;
          background: radial-gradient(55% 60% at 50% 0%, rgba(211,190,118,.14), transparent 70%);
          pointer-events: none;
        }
        .faq-intro-title { color: #fff; font-size: clamp(2.1rem, 3.4vw, 3.1rem); font-weight: 600; margin: 12px 0 14px; }
        .faq-intro-sub { color: rgba(255,255,255,.68); font-size: 15px; font-weight: 300; max-width: 560px; margin: 0 auto; line-height: 1.7; }
        @media (max-width: 640px) { .faq-intro { padding: 128px 0 48px; } }

        /* Category filter pills */
        .faq-filters { display: flex; flex-wrap: wrap; gap: 10px; justify-content: center; margin-top: 32px; position: relative; z-index: 1; }
        .faq-pill {
          font-family: 'DM Sans', sans-serif;
          font-size: 12.5px;
          font-weight: 600;
          letter-spacing: .2px;
          padding: 10px 20px;
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,.2);
          background: rgba(255,255,255,.06);
          color: rgba(255,255,255,.75);
          cursor: pointer;
          transition: all .2s ease;
        }
        .faq-pill:hover { border-color: rgba(211,190,118,.5); color: #fff; }
        .faq-pill.on { background: var(--gold); border-color: var(--gold); color: var(--teal-d); }

        /* Accordion */
        .faq-list { max-width: 780px; margin: 0 auto; display: flex; flex-direction: column; gap: 14px; }
        .faq-item {
          background: #fff;
          border: 1px solid rgba(41,75,90,.1);
          border-radius: 14px;
          overflow: hidden;
          transition: border-color .2s, box-shadow .2s;
        }
        .faq-item.open { border-color: rgba(211,190,118,.5); box-shadow: var(--shadow-sm); }
        .faq-q {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding: 22px 26px;
          background: none;
          border: none;
          text-align: left;
          cursor: pointer;
          font-family: 'DM Sans', sans-serif;
        }
        .faq-q-text { font-size: 15.5px; font-weight: 600; color: var(--ink); line-height: 1.4; }
        .faq-q-icon {
          flex-shrink: 0;
          width: 30px; height: 30px;
          border-radius: 50%;
          border: 1px solid rgba(41,75,90,.18);
          display: flex; align-items: center; justify-content: center;
          color: var(--gold-d);
          transition: transform .25s ease, border-color .2s, background .2s;
        }
        .faq-item.open .faq-q-icon { transform: rotate(45deg); background: var(--gold); border-color: var(--gold); color: var(--teal-d); }
        .faq-a-wrap { max-height: 0; overflow: hidden; transition: max-height .3s ease; }
        .faq-item.open .faq-a-wrap { max-height: 320px; }
        .faq-a { padding: 0 26px 24px; font-size: 14px; color: var(--silver); line-height: 1.75; font-weight: 300; }

        /* Support cards below the accordion */
        .support-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
        @media (max-width: 900px) { .support-grid { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 560px) { .support-grid { grid-template-columns: 1fr; } }
        .support-card { background: #fff; border: 1px solid rgba(41,75,90,.09); border-radius: var(--radius); padding: 28px 24px; transition: transform .3s, box-shadow .3s, border-color .3s; }
        .support-card:hover { transform: translateY(-4px); box-shadow: var(--shadow-sm); border-color: rgba(211,190,118,.35); }
        .support-icon { color: var(--gold-d); margin-bottom: 16px; display: block; }
        .support-title { font-size: 1.05rem; font-weight: 600; color: var(--ink); margin-bottom: 8px; }
        .support-text { font-size: 13.5px; color: var(--silver); line-height: 1.7; font-weight: 300; }

        /* Bottom CTA */
        .faq-cta { background: var(--teal-d); border-radius: 24px; padding: 56px 40px; text-align: center; position: relative; overflow: hidden; }
        .faq-cta::before {
          content: '';
          position: absolute; inset: 0;
          background: radial-gradient(60% 70% at 85% 100%, rgba(211,190,118,.12), transparent 70%);
          pointer-events: none;
        }
        .faq-cta-title { color: #fff; font-size: clamp(1.5rem, 2.4vw, 2rem); font-weight: 600; margin: 10px 0 12px; position: relative; }
        .faq-cta-sub { color: rgba(255,255,255,.65); font-size: 14px; font-weight: 300; max-width: 440px; margin: 0 auto 28px; line-height: 1.7; position: relative; }
        .faq-cta-actions { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; position: relative; }
        .cta-btn-primary {
          display: inline-flex; align-items: center; gap: 8px;
          background: var(--gold); color: var(--teal-d);
          border-radius: 5px; font-size: 12px; font-weight: 700; letter-spacing: .3px;
          padding: 14px 26px; text-decoration: none;
          transition: transform .2s, background .2s;
        }
        .cta-btn-primary:hover { background: #e2ce86; transform: translateY(-2px); color: var(--teal-d); }
        .cta-btn-ghost {
          display: inline-flex; align-items: center;
          background: rgba(255,255,255,.08); color: #fff;
          border: 1px solid rgba(255,255,255,.25);
          border-radius: 5px; font-size: 12px; font-weight: 600; letter-spacing: .3px;
          padding: 14px 24px; text-decoration: none;
          transition: background .2s, border-color .2s;
        }
        .cta-btn-ghost:hover { border-color: #fff; background: rgba(255,255,255,.16); }
      `}</style>

      <Header />

      <section className="faq-intro">
        <div className="container">
          <span className="eyebrow light">{t("faqEyebrow")}</span>
          <h1 className="faq-intro-title">{t("faqTitle")}</h1>
          <p className="faq-intro-sub">
            {t("faqSubtitle")}
          </p>

          <div className="faq-filters">
            <button
              className={`faq-pill ${activeCategory === "all" ? "on" : ""}`}
              onClick={() => setActiveCategory("all")}
            >
              {t("faqAllQuestions")}
            </button>
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                className={`faq-pill ${activeCategory === c.id ? "on" : ""}`}
                onClick={() => setActiveCategory(c.id)}
              >
                {t(`faqCategory${c.id.charAt(0).toUpperCase() + c.id.slice(1)}`)}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad bg-white-section" data-aos="fade-up">
        <div className="container">
          <div className="faq-list">
            {visibleItems.map((item) => {
              const isOpen = openId === item.id;
              return (
                <div key={item.id} className={`faq-item ${isOpen ? "open" : ""}`}>
                  <button
                    className="faq-q"
                    onClick={() => setOpenId(isOpen ? null : item.id)}
                    aria-expanded={isOpen}
                  >
                    <span className="faq-q-text">{item.q}</span>
                    <span className="faq-q-icon">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                    </span>
                  </button>
                  <div className="faq-a-wrap">
                    <p className="faq-a">{item.a}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <DuneRule />

      <section className="section-pad bg-cream-section" data-aos="fade-up">
        <div className="container">
          <div className="text-center" style={{ marginBottom: 40 }}>
            <span className="eyebrow">{t("faqBeyondEyebrow")}</span>
            <h2 className="section-title">{t("faqBeyondTitle")}</h2>
            <p className="section-sub" style={{ margin: "0 auto" }}>
              {t("faqBeyondSub")}
            </p>
          </div>
          <div className="support-grid">
            <div className="support-card" data-aos="fade-up" data-aos-delay="0">
              <span className="support-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>
              </span>
              <div className="support-title">{t("faqSupport1Title")}</div>
              <p className="support-text">{t("faqSupport1Text")}</p>
            </div>
            <div className="support-card" data-aos="fade-up" data-aos-delay="90">
              <span className="support-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
              </span>
              <div className="support-title">{t("faqSupport2Title")}</div>
              <p className="support-text">{t("faqSupport2Text")}</p>
            </div>
            <div className="support-card" data-aos="fade-up" data-aos-delay="180">
              <span className="support-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" /></svg>
              </span>
              <div className="support-title">{t("faqSupport3Title")}</div>
              <p className="support-text">{t("faqSupport3Text")}</p>
            </div>
            <div className="support-card" data-aos="fade-up" data-aos-delay="270">
              <span className="support-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5z" /><path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" /></svg>
              </span>
              <div className="support-title">{t("faqSupport4Title")}</div>
              <p className="support-text">{t("faqSupport4Text")}</p>
            </div>
            <div className="support-card" data-aos="fade-up" data-aos-delay="360">
              <span className="support-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" /></svg>
              </span>
              <div className="support-title">{t("faqSupport5Title")}</div>
              <p className="support-text">{t("faqSupport5Text")}</p>
            </div>
            <div className="support-card" data-aos="fade-up" data-aos-delay="450">
              <span className="support-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></svg>
              </span>
              <div className="support-title">{t("faqSupport6Title")}</div>
              <p className="support-text">{t("faqSupport6Text")}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section-pad bg-white-section" data-aos="fade-up">
        <div className="container">
          <div className="faq-cta">
            <span className="eyebrow light">{t("faqCtaEyebrow")}</span>
            <h2 className="faq-cta-title">{t("faqCtaTitle")}</h2>
            <p className="faq-cta-sub">
              {t("faqCtaSub")}
            </p>
            <div className="faq-cta-actions">
              <a href="/contact" className="cta-btn-primary">
                {t("faqContactBtn")}
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                </svg>
              </a>
              <a href="https://wa.me/96893579571" className="cta-btn-ghost" target="_blank" rel="noopener noreferrer">
                {t("faqWhatsappBtn")}
              </a>
            </div>
          </div>
        </div>
      </section>

      <DefaultFooter />
    </>
  );
}

export default FAQ;